import { useCallback, useEffect, useState } from "react";

import type { SortState } from "@/components/shared/use-table-sort";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { readSearchList, readSearchString, readUrlParams, writeUrlParams } from "@/lib/list-search";

/** The cursor request shape every list hook (usePersons, useRoles, …) accepts. */
export interface PageRequest {
  cursor: string | null;
  direction: "next" | "prev";
}

/** The subset of a cursor-paginated list response this hook reads. */
interface CursorPage {
  nextCursor?: string | null;
  prevCursor?: string | null;
  hasNext?: boolean;
  hasPrev?: boolean;
  total?: number;
}

/** Props ready to spread onto `<CursorPagination>` (minus `limit`/`loading`/`itemLabel`). */
export interface PaginationBinding {
  total: number;
  page: number;
  hasNext: boolean;
  hasPrev: boolean;
  onNext: () => void;
  onPrev: () => void;
}

/** A filter value the hook can round-trip through the URL. */
type FilterValue = string[] | string | null;

interface UseCursorListOptions<F extends Record<string, FilterValue>> {
  /** Initial sort column key. */
  sortKey: string;
  /** Initial sort direction (default "asc"). */
  sortDir?: SortState["dir"];
  /** Debounce applied to the search box before it hits the query (default 300ms). */
  searchDelayMs?: number;
  /**
   * The list's filters and their defaults. Array defaults round-trip as CSV,
   * scalar defaults as a plain value; the type of each default determines how
   * the URL value is parsed back. Keeps filters URL-persisted alongside search
   * and sort so a filtered view survives navigation and is shareable.
   */
  defaultFilters?: F;
}

/** Order-insensitive "is this array filter at its default?" comparison. */
function isDefaultList(value: string[], def: FilterValue): boolean {
  const defList = Array.isArray(def) ? def : [];
  return [...value].sort().join(",") === [...defList].sort().join(",");
}

/**
 * Hydrate the filters record from the URL, falling back to each default.
 *
 * For array filters, param PRESENCE is authoritative, not non-emptiness: a
 * present-but-empty param (`statuses=`) means the user cleared a non-empty
 * default (e.g. Status "All"), and must not revert to the default on Back.
 * Only an absent param falls back to the default.
 */
function hydrateFilters<F extends Record<string, FilterValue>>(
  defaults: F,
  urlSearch: Record<string, string>,
): F {
  const out = { ...defaults };
  for (const key of Object.keys(defaults) as (keyof F)[]) {
    const def = defaults[key];
    const raw = urlSearch[key as string];
    if (Array.isArray(def)) {
      if ((key as string) in urlSearch) out[key] = readSearchList(raw) as F[keyof F];
    } else {
      const value = readSearchString(raw);
      if (value !== undefined) out[key] = value as F[keyof F];
    }
  }
  return out;
}

/**
 * Serialize search + sort + filters into a flat string search-param record.
 * Array filters are written whenever they differ from their default — a
 * cleared non-empty default serializes as an empty param (`statuses=`) so
 * hydration can tell "cleared" apart from "absent"; a value equal to the
 * default is omitted, keeping the pristine URL clean.
 */
function serialize(
  search: string,
  sort: SortState,
  sortKey: string,
  sortDir: SortState["dir"],
  filters: Record<string, FilterValue>,
  defaults: Record<string, FilterValue>,
): Record<string, string> {
  const out: Record<string, string> = {};
  if (search) out.q = search;
  // Only persist a non-default sort, so a pristine view has a clean URL.
  if (sort.key !== sortKey || sort.dir !== sortDir) {
    out.sort = sort.key;
    out.dir = sort.dir;
  }
  for (const [key, value] of Object.entries(filters)) {
    if (Array.isArray(value)) {
      if (!isDefaultList(value, defaults[key] ?? null)) out[key] = value.join(",");
    } else if (value) {
      out[key] = value;
    }
  }
  return out;
}

/**
 * The shared search + sort + cursor-pagination machinery every directory list
 * repeated by hand (Persons/Roles/Skills/Competencies/Certifications). It owns
 * the debounced search box, the sort toggle, the caller's filters, and the
 * page/cursor state — and mirrors search/sort/filters into the URL so a
 * filtered view survives Back and is shareable/bookmarkable.
 *
 * The URL is written from state on change; state is read from the URL only at
 * mount (the list re-mounts when you return from a detail page), so there is no
 * state<->URL sync loop. Paging is deliberately NOT persisted — an opaque
 * cursor can't be safely restored, so a restored view starts on page 1.
 */
export function useCursorList<F extends Record<string, FilterValue> = Record<string, never>>({
  sortKey,
  sortDir = "asc",
  searchDelayMs = 300,
  defaultFilters = {} as F,
}: UseCursorListOptions<F>) {
  const urlSearch = readUrlParams();

  // Capture the defaults once — callers pass inline literals, and hydration /
  // reset / URL serialization must all compare against the same stable object.
  const [defaults] = useState(defaultFilters);

  // Initial state from the URL — runs once (useState initializer). Restores the
  // exact view when the list re-mounts after Back.
  const [search, setSearchRaw] = useState(() => readSearchString(urlSearch.q) ?? "");
  const debouncedSearch = useDebouncedValue(search, searchDelayMs);
  const [sort, setSort] = useState<SortState>(() => ({
    key: readSearchString(urlSearch.sort) ?? sortKey,
    dir: readSearchString(urlSearch.dir) === "desc" ? "desc" : sortDir,
  }));
  const [filters, setFilters] = useState<F>(() => hydrateFilters(defaults, urlSearch));
  const [page, setPage] = useState(1);
  const [pageRequest, setPageRequest] = useState<PageRequest>({ cursor: null, direction: "next" });

  const resetPage = useCallback(() => {
    setPage(1);
    setPageRequest({ cursor: null, direction: "next" });
  }, []);

  const setSearch = useCallback(
    (value: string) => {
      setSearchRaw(value);
      resetPage();
    },
    [resetPage],
  );

  const onSort = useCallback(
    (key: string) => {
      setSort((s) =>
        s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" },
      );
      resetPage();
    },
    [resetPage],
  );

  const setFilter = useCallback(
    <K extends keyof F>(key: K, value: F[K]) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
      resetPage();
    },
    [resetPage],
  );

  // Reset every filter back to its DEFAULT (not empty) and clear the search —
  // so a list whose Status defaults to "Active" returns to Active, not "All".
  const resetFilters = useCallback(() => {
    setFilters(defaults);
    setSearchRaw("");
    resetPage();
  }, [defaults, resetPage]);

  // Mirror the view into the URL whenever it changes. Writing only — never read
  // back into state — so there's no loop. replaceState keeps one history entry
  // per list visit; navigating to a detail pushes a new entry, and Back returns
  // to this URL, re-mounting the list which re-reads the params above.
  useEffect(() => {
    writeUrlParams(serialize(debouncedSearch, sort, sortKey, sortDir, filters, defaults));
  }, [debouncedSearch, sort, filters, sortKey, sortDir, defaults]);

  const pagination = useCallback(
    (data: CursorPage | undefined): PaginationBinding => ({
      total: data?.total ?? 0,
      page,
      hasNext: data?.hasNext ?? false,
      hasPrev: data?.hasPrev ?? false,
      onNext: () => {
        if (data?.nextCursor) {
          setPage((p) => p + 1);
          setPageRequest({ cursor: data.nextCursor ?? null, direction: "next" });
        }
      },
      onPrev: () => {
        if (data?.prevCursor) {
          setPage((p) => Math.max(1, p - 1));
          setPageRequest({ cursor: data.prevCursor ?? null, direction: "prev" });
        }
      },
    }),
    [page],
  );

  return {
    search,
    setSearch,
    debouncedSearch,
    sort,
    onSort,
    filters,
    setFilter,
    resetFilters,
    page,
    pageRequest,
    resetPage,
    pagination,
  };
}
